/**
 * Waterfall Chart component for visualizing OpenTelemetry-style trace spans.
 * Displays a hierarchical timeline showing parent-child relationships and timing.
 *
 * @module components/ui/WaterfallChart
 */

import { useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { ChevronRight, ChevronDown } from 'lucide-react';
import type { PerfTrace } from '@/types';

export interface WaterfallChartProps {
  /** The performance traces to display */
  traces: PerfTrace[];
  /** The trace ID for this request */
  traceId?: string;
  /** Client-side round-trip duration in milliseconds (browser request to response) */
  clientDurationMs?: number;
  /** Optional CSS class name */
  className?: string;
}

/**
 * Color palette for spans by service/component
 */
const SERVICE_COLORS: Record<string, { bg: string; border: string; text: string }> = {
  GlobalApi: {
    bg: 'bg-blue-500',
    border: 'border-blue-600',
    text: 'text-blue-600 dark:text-blue-400',
  },
  ClusterApi: {
    bg: 'bg-purple-500',
    border: 'border-purple-600',
    text: 'text-purple-600 dark:text-purple-400',
  },
  NodeAgent: {
    bg: 'bg-green-500',
    border: 'border-green-600',
    text: 'text-green-600 dark:text-green-400',
  },
  InstanceShim: {
    bg: 'bg-amber-500',
    border: 'border-amber-600',
    text: 'text-amber-600 dark:text-amber-400',
  },
  InstanceAgent: {
    bg: 'bg-red-500',
    border: 'border-red-600',
    text: 'text-red-600 dark:text-red-400',
  },
};

const DEFAULT_COLOR = {
  bg: 'bg-gray-500',
  border: 'border-gray-600',
  text: 'text-gray-600 dark:text-gray-400',
};

/**
 * Extract service name from span name (e.g., "NodeAgent.InjectVmm" -> "NodeAgent")
 */
function getServiceFromName(name: string): string {
  const dotIndex = name.indexOf('.');
  if (dotIndex > 0) {
    return name.substring(0, dotIndex);
  }
  return name;
}

/**
 * Get color configuration for a span
 */
function getSpanColor(name: string): (typeof SERVICE_COLORS)[string] {
  const service = getServiceFromName(name);
  return SERVICE_COLORS[service] || DEFAULT_COLOR;
}

/**
 * Format milliseconds for display
 */
function formatDuration(ms: number): string {
  if (ms < 1) {
    return `${ms.toFixed(2)}ms`;
  }
  if (ms < 1000) {
    return `${ms.toFixed(1)}ms`;
  }
  return `${(ms / 1000).toFixed(2)}s`;
}

/**
 * Format a span name for display (remove service prefix)
 */
function formatSpanName(name: string): string {
  const dotIndex = name.indexOf('.');
  if (dotIndex > 0) {
    return name.substring(dotIndex + 1);
  }
  return name;
}

/**
 * Tree node representing a span with its children
 */
interface SpanNode {
  trace: PerfTrace;
  children: SpanNode[];
  depth: number;
}

/**
 * Build a tree structure from flat traces using parentId
 */
function buildSpanTree(traces: PerfTrace[]): SpanNode[] {
  const nodeMap = new Map<string, SpanNode>();
  const roots: SpanNode[] = [];

  // Create nodes for all traces
  for (const trace of traces) {
    nodeMap.set(trace.id, {
      trace,
      children: [],
      depth: 0,
    });
  }

  // Build parent-child relationships
  for (const trace of traces) {
    const node = nodeMap.get(trace.id)!;
    if (trace.parentId && nodeMap.has(trace.parentId)) {
      const parent = nodeMap.get(trace.parentId)!;
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  }

  // Sort children by startMs and calculate depths
  function sortAndSetDepth(nodes: SpanNode[], depth: number) {
    nodes.sort((a, b) => a.trace.startMs - b.trace.startMs);
    for (const node of nodes) {
      node.depth = depth;
      sortAndSetDepth(node.children, depth + 1);
    }
  }

  sortAndSetDepth(roots, 0);

  return roots;
}

/**
 * Flatten tree to array for rendering, preserving hierarchy info
 */
interface FlatSpan {
  node: SpanNode;
  hasChildren: boolean;
}

function flattenTree(roots: SpanNode[]): FlatSpan[] {
  const result: FlatSpan[] = [];

  function traverse(nodes: SpanNode[]) {
    for (const node of nodes) {
      result.push({
        node,
        hasChildren: node.children.length > 0,
      });
      traverse(node.children);
    }
  }

  traverse(roots);
  return result;
}

/**
 * Waterfall Chart component
 */
export function WaterfallChart({
  traces,
  traceId,
  clientDurationMs,
  className,
}: WaterfallChartProps) {
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(new Set());

  // Build tree and calculate timeline bounds
  const { flatSpans, timelineStart, totalDuration } = useMemo(() => {
    if (traces.length === 0) {
      return { tree: [], flatSpans: [], timelineStart: 0, timelineEnd: 0, totalDuration: 0 };
    }

    const tree = buildSpanTree(traces);
    const flatSpans = flattenTree(tree);

    // Calculate timeline bounds from all traces
    let minStart = Infinity;
    let maxEnd = -Infinity;

    for (const trace of traces) {
      minStart = Math.min(minStart, trace.startMs);
      maxEnd = Math.max(maxEnd, trace.startMs + trace.durationMs);
    }

    return {
      tree,
      flatSpans,
      timelineStart: minStart,
      timelineEnd: maxEnd,
      totalDuration: maxEnd - minStart,
    };
  }, [traces]);

  // Filter out collapsed children
  const visibleSpans = useMemo(() => {
    const result: FlatSpan[] = [];
    const skipChildren = new Set<string>();

    for (const flatSpan of flatSpans) {
      // Check if any ancestor is collapsed
      let skip = false;
      let current: SpanNode | undefined = flatSpan.node;
      while (current) {
        if (skipChildren.has(current.trace.id)) {
          skip = true;
          break;
        }
        // Find parent
        const parentId: string | undefined = current.trace.parentId;
        current = parentId
          ? flatSpans.find((f: FlatSpan) => f.node.trace.id === parentId)?.node
          : undefined;
      }

      if (!skip) {
        result.push(flatSpan);
        if (collapsedIds.has(flatSpan.node.trace.id)) {
          skipChildren.add(flatSpan.node.trace.id);
        }
      }
    }

    return result;
  }, [flatSpans, collapsedIds]);

  const toggleCollapse = (id: string) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  if (traces.length === 0) {
    return null;
  }

  return (
    <div className={clsx('space-y-4', className)}>
      {/* Header with OTEL trace ID */}
      {traceId && (
        <div className="flex items-center gap-2 text-sm">
          <span className="font-semibold text-gray-700 dark:text-gray-300">OtelTraceId:</span>
          <code className="font-mono text-xs bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded-sm">
            {traceId}
          </code>
        </div>
      )}

      {/* Summary stats */}
      <div className="flex items-center gap-6 text-sm">
        {clientDurationMs !== undefined && (
          <div>
            <span className="text-gray-500 dark:text-gray-400">Client Duration: </span>
            <span className="font-semibold text-gray-900 dark:text-gray-100">
              {formatDuration(clientDurationMs)}
            </span>
          </div>
        )}
        <div>
          <span className="text-gray-500 dark:text-gray-400">Server Duration: </span>
          <span className="font-semibold text-gray-900 dark:text-gray-100">
            {formatDuration(totalDuration)}
          </span>
        </div>
        <div>
          <span className="text-gray-500 dark:text-gray-400">Spans: </span>
          <span className="font-semibold text-gray-900 dark:text-gray-100">{traces.length}</span>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-xs">
        {Object.entries(SERVICE_COLORS).map(([service, colors]) => (
          <div key={service} className="flex items-center gap-1.5">
            <div className={clsx('w-3 h-3 rounded-sm', colors.bg)} />
            <span className="text-gray-600 dark:text-gray-400">{service}</span>
          </div>
        ))}
      </div>

      {/* Timeline header with time markers */}
      <div className="flex items-center gap-2">
        <div className="w-64 shrink-0" /> {/* Spacer for name column */}
        <div className="flex-1 relative h-6">
          <div className="absolute inset-0 flex justify-between text-xs text-gray-400">
            <span>0ms</span>
            <span>{formatDuration(totalDuration / 4)}</span>
            <span>{formatDuration(totalDuration / 2)}</span>
            <span>{formatDuration((totalDuration * 3) / 4)}</span>
            <span>{formatDuration(totalDuration)}</span>
          </div>
        </div>
        <div className="w-20 shrink-0" /> {/* Spacer for duration column */}
      </div>

      {/* Waterfall rows */}
      <div className="space-y-0.5">
        {visibleSpans.map(({ node, hasChildren }) => {
          const { trace, depth } = node;
          const colors = getSpanColor(trace.name);
          const isCollapsed = collapsedIds.has(trace.id);

          // Calculate bar position and width
          const leftPercent = ((trace.startMs - timelineStart) / totalDuration) * 100;
          const widthPercent = Math.max((trace.durationMs / totalDuration) * 100, 0.5); // Min 0.5% for visibility

          return (
            <div
              key={trace.id}
              className="flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-800/50 py-0.5 rounded-sm"
            >
              {/* Span name with indent */}
              <div
                className="w-64 shrink-0 flex items-center gap-1 overflow-hidden"
                style={{ paddingLeft: `${depth * 16}px` }}
              >
                {hasChildren ? (
                  <button
                    onClick={() => toggleCollapse(trace.id)}
                    className="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-sm"
                  >
                    {isCollapsed ? (
                      <ChevronRight className="w-3 h-3 text-gray-500" />
                    ) : (
                      <ChevronDown className="w-3 h-3 text-gray-500" />
                    )}
                  </button>
                ) : (
                  <span className="w-4" /> // Spacer when no children
                )}
                <span className={clsx('text-sm truncate', colors.text)} title={trace.name}>
                  {formatSpanName(trace.name)}
                </span>
              </div>

              {/* Timeline bar */}
              <div className="flex-1 relative h-6 bg-gray-100 dark:bg-gray-800 rounded-sm overflow-hidden">
                {/* Grid lines */}
                <div className="absolute inset-0 flex">
                  {[0.25, 0.5, 0.75].map((pos) => (
                    <div
                      key={pos}
                      className="absolute top-0 bottom-0 w-px bg-gray-200 dark:bg-gray-700"
                      style={{ left: `${pos * 100}%` }}
                    />
                  ))}
                </div>
                {/* Span bar */}
                <div
                  className={clsx(
                    'absolute top-1 bottom-1 rounded-xs transition-all',
                    colors.bg,
                    'opacity-80 hover:opacity-100'
                  )}
                  style={{
                    left: `${leftPercent}%`,
                    width: `${widthPercent}%`,
                    minWidth: '2px',
                  }}
                  title={`${trace.name}: ${formatDuration(trace.durationMs)}`}
                />
              </div>

              {/* Duration */}
              <div className="w-20 shrink-0 text-right">
                <span className="text-sm font-mono text-gray-600 dark:text-gray-400">
                  {formatDuration(trace.durationMs)}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Detailed breakdown table */}
      <details className="mt-4">
        <summary className="cursor-pointer text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
          View detailed breakdown
        </summary>
        <div className="mt-2 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left py-2 px-3 text-gray-500 dark:text-gray-400 font-medium">
                  Span
                </th>
                <th className="text-left py-2 px-3 text-gray-500 dark:text-gray-400 font-medium">
                  ID
                </th>
                <th className="text-left py-2 px-3 text-gray-500 dark:text-gray-400 font-medium">
                  Parent ID
                </th>
                <th className="text-right py-2 px-3 text-gray-500 dark:text-gray-400 font-medium">
                  Start
                </th>
                <th className="text-right py-2 px-3 text-gray-500 dark:text-gray-400 font-medium">
                  Duration
                </th>
              </tr>
            </thead>
            <tbody>
              {traces
                .slice()
                .sort((a, b) => a.startMs - b.startMs)
                .map((trace) => {
                  const colors = getSpanColor(trace.name);
                  return (
                    <tr
                      key={trace.id}
                      className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                    >
                      <td className={clsx('py-2 px-3', colors.text)}>{trace.name}</td>
                      <td className="py-2 px-3 font-mono text-xs text-gray-500">{trace.id}</td>
                      <td className="py-2 px-3 font-mono text-xs text-gray-500">
                        {trace.parentId || '—'}
                      </td>
                      <td className="py-2 px-3 text-right font-mono text-gray-600 dark:text-gray-400">
                        +{formatDuration(trace.startMs - timelineStart)}
                      </td>
                      <td className="py-2 px-3 text-right font-mono text-gray-700 dark:text-gray-300">
                        {formatDuration(trace.durationMs)}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}
